import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronDown, ChevronUp, Check, X, Pencil, RefreshCw, ShieldAlert } from "lucide-react";
import { http, formatApiError } from "../lib/api";

const SEVERITY = {
  incorrect: { label: "Incorrect", cls: "bg-[#8a2a1d] text-[#f8f4ec]" },
  imprecise: { label: "Imprecise", cls: "bg-[#c9a45c] text-[#151e1a]" },
};

function IssueRow({ slug, issue, onChanged }) {
  const [busy, setBusy] = useState(null);
  const act = async (action) => {
    setBusy(action);
    try {
      await http.post(`/admin/fact-checks/${slug}/issues/${issue.id}/${action}`);
      toast.success(action === "apply" ? "Correction applied." : "Issue dismissed.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };
  const done = issue.applied || issue.dismissed;
  return (
    <li
      className={`border-t px-5 py-4 grid grid-cols-12 gap-4 ${done ? "opacity-60" : ""}`}
      style={{ borderColor: "var(--border)" }}
      data-testid={`fc-issue-${issue.id}`}
    >
      <div className="col-span-12 md:col-span-2 flex flex-wrap gap-1.5 content-start">
        <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 ${SEVERITY[issue.severity]?.cls || ""}`}>
          {SEVERITY[issue.severity]?.label || issue.severity}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-[#f1eadb] text-[#404e48]">
          {issue.confidence} · {issue.category}
        </span>
        {issue.applied && <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-[#1e4a35] text-white">Applied</span>}
        {issue.dismissed && <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-[#66726d] text-white">Dismissed</span>}
      </div>
      <div className="col-span-12 md:col-span-8 space-y-2 text-sm">
        <p className="text-[#8a2a1d] line-through decoration-[#8a2a1d]/50">{issue.quote}</p>
        <p className="text-[#1e4a35] font-medium">{issue.correction}</p>
        <p className="text-[#66726d] text-xs leading-relaxed">{issue.rationale}</p>
        {!issue.quote_found && !issue.applied && (
          <p className="text-xs text-[#8a2a1d]">Quoted text not found verbatim — apply manually in the editor.</p>
        )}
      </div>
      <div className="col-span-12 md:col-span-2 flex md:flex-col gap-2 md:items-end">
        {!done && (
          <>
            <button
              onClick={() => act("apply")}
              disabled={busy || !issue.quote_found}
              className="btn-primary !px-3 !py-1.5 !text-[10px] inline-flex items-center gap-1 disabled:opacity-40"
              data-testid={`fc-apply-${issue.id}`}
            >
              <Check size={12} /> {busy === "apply" ? "Applying…" : "Apply fix"}
            </button>
            <button
              onClick={() => act("dismiss")}
              disabled={busy}
              className="btn-secondary !px-3 !py-1.5 !text-[10px] inline-flex items-center gap-1"
              data-testid={`fc-dismiss-${issue.id}`}
            >
              <X size={12} /> Dismiss
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function PostRow({ item, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rerunning, setRerunning] = useState(false);

  const load = () =>
    http
      .get(`/admin/fact-checks/${item.slug}`)
      .then(({ data }) => setDetail(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));

  const toggle = () => {
    if (!open && !detail) load();
    setOpen((o) => !o);
  };

  const changed = () => {
    load();
    onChanged();
  };

  const rerun = async () => {
    setRerunning(true);
    try {
      await http.post(`/admin/fact-checks/${item.slug}/rerun`);
      toast.success("Re-checked.");
      changed();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRerunning(false);
    }
  };

  return (
    <div className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`fc-post-${item.slug}`}>
      <button
        onClick={toggle}
        className="w-full grid grid-cols-12 gap-4 px-5 py-4 items-center text-left hover:bg-[#f1eadb]/60 transition-colors"
        data-testid={`fc-toggle-${item.slug}`}
      >
        <div className="col-span-12 md:col-span-6 min-w-0">
          <p className="font-serif text-lg text-[#151e1a] truncate">{item.title}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
            {item.post_status} · {item.claims_reviewed} claims reviewed
            {item.error && <span className="text-[#8a2a1d]"> · check failed</span>}
          </p>
        </div>
        <div className="col-span-8 md:col-span-4 flex gap-2 font-mono text-[10px] uppercase tracking-widest">
          <span className="px-2 py-0.5 bg-[#8a2a1d] text-[#f8f4ec]">{item.incorrect} incorrect</span>
          <span className="px-2 py-0.5 bg-[#c9a45c] text-[#151e1a]">{item.imprecise} imprecise</span>
          <span className="px-2 py-0.5 bg-[#1e4a35] text-white">{item.applied} applied</span>
          {item.health_flags > 0 && (
            <span className="px-2 py-0.5 bg-[#f1eadb] text-[#404e48] inline-flex items-center gap-1">
              <ShieldAlert size={10} /> {item.health_flags} health
            </span>
          )}
        </div>
        <div className="col-span-4 md:col-span-2 flex justify-end items-center gap-3 text-[#66726d]">
          <span className="font-mono text-[10px] uppercase tracking-widest">{item.open_issues} open</span>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>
      {open && (
        <div className="bg-[#fdfbf6]">
          <div className="px-5 py-3 flex flex-wrap gap-3 items-center justify-end border-t" style={{ borderColor: "var(--border)" }}>
            <Link to={`/admin/posts/${item.post_id}/edit`} className="btn-secondary !px-3 !py-1.5 !text-[10px] inline-flex items-center gap-1" data-testid={`fc-edit-${item.slug}`}>
              <Pencil size={12} /> Open in editor
            </Link>
            <button onClick={rerun} disabled={rerunning} className="btn-secondary !px-3 !py-1.5 !text-[10px] inline-flex items-center gap-1" data-testid={`fc-rerun-${item.slug}`}>
              <RefreshCw size={12} className={rerunning ? "animate-spin" : ""} /> {rerunning ? "Checking… (takes a few minutes)" : "Re-check"}
            </button>
          </div>
          <PostIssues detail={detail} slug={item.slug} onChanged={changed} />
        </div>
      )}
    </div>
  );
}

function PostIssues({ detail, slug, onChanged }) {
  if (!detail) {
    return <p className="px-5 py-6 font-mono text-[10px] uppercase tracking-widest text-[#66726d] animate-pulse">loading…</p>;
  }
  if (detail.issues.length === 0) {
    return <p className="px-5 py-6 text-sm text-[#66726d] italic">No issues found — this article checked out clean.</p>;
  }
  return (
    <ul>
      {detail.issues.map((i) => (
        <IssueRow key={i.id} slug={slug} issue={i} onChanged={onChanged} />
      ))}
    </ul>
  );
}

function matchesFilter(item, filter) {
  if (filter === "open") return item.open_issues > 0;
  if (filter === "clean") return item.total_issues === 0;
  return true;
}

function PostRows({ data, items, onChanged }) {
  if (!data) {
    return <p className="p-8 text-center font-mono text-[10px] uppercase tracking-widest text-[#66726d] animate-pulse">loading…</p>;
  }
  if (items.length === 0) return <p className="p-10 text-center text-sm text-[#66726d] italic">Nothing here.</p>;
  return items.map((item) => <PostRow key={item.id} item={item} onChanged={onChanged} />);
}

export default function AdminFactChecks() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("open");

  const load = () =>
    http
      .get("/admin/fact-checks")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!data?.running) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [data?.running]);

  const items = (data?.items || []).filter((i) => matchesFilter(i, filter));
  const totals = (data?.items || []).reduce(
    (a, i) => ({ issues: a.issues + i.total_issues, open: a.open + i.open_issues, applied: a.applied + i.applied, incorrect: a.incorrect + i.incorrect }),
    { issues: 0, open: 0, applied: 0, incorrect: 0 }
  );

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-14" data-testid="admin-fact-checks-page">
      <Link to="/admin" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#66726d] hover:text-[#1e4a35]" data-testid="fc-back-link">
        <ChevronLeft size={12} /> Dashboard
      </Link>
      <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="overline">Editorial</p>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl font-light tracking-tight">Fact checks</h1>
          <p className="mt-3 text-sm text-[#404e48] max-w-xl">
            Every article is audited by GPT-5 (a different model from the author). High-confidence factual errors are applied automatically; everything else waits for your call.
          </p>
        </div>
        {data && (
          <div className="grid grid-cols-4 gap-4 font-mono text-[10px] uppercase tracking-widest text-[#66726d]" data-testid="fc-stats">
            <div><p className="font-serif text-3xl text-[#151e1a] normal-case tracking-normal">{data.checked}<span className="text-[#66726d] text-lg">/{data.total_posts}</span></p>checked{data.running && <span className="block text-[#1e4a35] animate-pulse">running…</span>}</div>
            <div><p className="font-serif text-3xl text-[#151e1a]">{totals.issues}</p>flags</div>
            <div><p className="font-serif text-3xl text-[#8a2a1d]">{totals.incorrect}</p>incorrect</div>
            <div><p className="font-serif text-3xl text-[#1e4a35]">{totals.applied}</p>applied</div>
          </div>
        )}
      </div>

      <div className="mt-8 flex gap-2">
        {[["open", "Needs review"], ["all", "All checked"], ["clean", "Clean"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border rounded-full transition-colors ${filter === k ? "bg-[#1e4a35] text-white border-[#1e4a35]" : "border-[#e2d9c6] text-[#404e48] hover:border-[#1e4a35]"}`}
            data-testid={`fc-filter-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4 border" style={{ borderColor: "var(--border)" }}>
        <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-[#f1eadb] font-mono text-[10px] uppercase tracking-widest text-[#404e48]">
          <div className="col-span-6">Article</div>
          <div className="col-span-4">Findings</div>
          <div className="col-span-2 text-right">Open</div>
        </div>
        <PostRows data={data} items={items} onChanged={load} />
      </div>
    </div>
  );
}
